import * as React from 'react'
import { AnimatePresence, m } from 'framer-motion'
import { tw } from 'utils/classnames'

type TopBannerVariant = 'primary' | 'secondary' | 'danger' | 'aurora'

const VARIANT_CLASSES: Record<TopBannerVariant, string> = {
  primary: 'bg-themed-violet-1000 border-b-themed-violet-800 border-b',
  secondary: 'bg-themed-violet-1000 border-b-themed-violet-800 border-b',
  danger: 'bg-paradise-pink',
  aurora:
    'bg-[linear-gradient(140deg,#832499_0%,#fa553a_58%,#d6047a_100%)] md:bg-[linear-gradient(90deg,#832499_0%,#aa2680_10%,#c72d6c_20%,#da3854_30%,#e74149_40%,#f04541_50%,#f64e48_60%,#fa553a_66%,#ea3657_75%,#e72161_81%,#e1166a_92%,#d6047a_100%)]',
}

interface TopBannerRootProps {
  visible?: boolean
  variant?: TopBannerVariant
  className?: string
  children?: React.ReactNode
}

function TopBannerRoot({ visible = true, variant, className, children }: TopBannerRootProps) {
  return (
    <AnimatePresence initial={false}>
      {visible ? (
        <m.div
          initial={{ height: 'auto' }}
          animate={{ height: 'auto' }}
          exit={{ height: 0, minHeight: 0 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className={tw(
            'shrink-0 sticky left-0 top-0 top-banner',
            variant && VARIANT_CLASSES[variant],
            className,
          )}
        >
          <m.div
            initial={{ opacity: 1, scaleY: 1 }}
            animate={{ opacity: 1, scaleY: 1 }}
            exit={{ opacity: 0, scaleY: 0 }}
            style={{ transformOrigin: 'top', overflow: 'hidden' }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
            className="p-3 flex flex-row items-center justify-between gap-2 overflow-hidden"
          >
            {children}
          </m.div>
        </m.div>
      ) : null}
    </AnimatePresence>
  )
}

function TopBannerLeft({ className, children, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={tw(
        'px-2 flex items-center gap-2 typography-14 text-service-white-white shrink grow',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

function TopBannerRight({ className, children, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={tw('flex items-center justify-center flex-row md:gap-6 shrink grow', className)}
      {...props}
    >
      {children}
    </div>
  )
}

function TopBannerCenter({ className, children, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={tw(
        'px-2 flex flex-col lg:flex-row lg:items-center justify-center gap-2 typography-14 text-service-white-white grow',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export const TopBanner = Object.assign(TopBannerRoot, {
  Left: TopBannerLeft,
  Center: TopBannerCenter,
  Right: TopBannerRight,
})
